import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { selectBlueprintById } from '~/Slices/BlueprintsSlice';
import type { FetchProductsByProductNumberParams } from '~/Slices/ProductHeadersSlice';
import {
	fetchProductsByProductNumber,
	selectProductHeader,
	selectProductHeaders,
} from '~/Slices/ProductHeadersSlice';
import { selectSupplierById } from '~/Slices/SuppliersSlice';
import type { IProductHeader } from '~/Slices/schema';
import { Indicator } from '~/components/AddBySupcModal/ProductValidationIndicator';
import type { IBlueprint, ISupplier } from '~/domain';
import type { IAppState } from '~/appstate';

export interface IAddBySupcModalState {
	showAddBySupcModal: boolean;
	submitEnabled: boolean;
	validationInProgress: boolean;
	blueprintId: number;
	corpSupplierCode: string;
	productNumbers: string[];
	productValidation: Indicator[];
	productDetails: string[];
}

export const initialState: IAddBySupcModalState = {
	showAddBySupcModal: false,
	submitEnabled: false,
	validationInProgress: false,
	blueprintId: null,
	corpSupplierCode: null,
	productNumbers: [],
	productValidation: [],
	productDetails: [],
};

export interface ISetProductNumbersArgs {
	blueprintId: number;
	productNumbers: string[];
}

export const open = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'addBySupcModal/openModal',
	async (blueprintId: number, { dispatch, getState }) => {
		const state: IAppState = getState();

		const blueprint: IBlueprint = selectBlueprintById(state, blueprintId);
		const supplierId: number = blueprint.supplierId;
		const currentSupplier: ISupplier = selectSupplierById(state, supplierId);
		const corpSupplierCode: string = currentSupplier.dssiCode;

		dispatch(
			addBySupcModalSlice.actions.showAddBySupcModal({
				blueprintId,
				corpSupplierCode,
			})
		);
	}
);

export const validateProductNumbers = createAsyncThunk<
	void,
	FetchProductsByProductNumberParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'addBySupcModal/validateProductNumbers',
	async (params: FetchProductsByProductNumberParams, { dispatch }) => {
		await dispatch(fetchProductsByProductNumber(params));
	}
);

export const updateProductValidationIndicators = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'addBySupcModal/updateProductValidationIndicators',
	async (_, { dispatch, getState }) => {
		const state: IAppState = getState();
		const { productNumbers } = state.addBySupcModal;
		const updatedProductValidation: Indicator[] = [];
		const updatedProductDetails: string[] = [];

		for (let i = 0; i < productNumbers.length; i++) {
			const productNumber = productNumbers[i];
			if (productNumber.trim() !== '') {
				const productId = getProductID(state, productNumber);
				const productHeader = selectProductHeader(state, productId);
				if (productHeader) {
					updatedProductValidation[i] = Indicator.Valid;
					updatedProductDetails[i] = productHeader.description;
				} else {
					updatedProductValidation[i] = Indicator.Invalid;
					updatedProductDetails[i] = '--';
				}
			} else {
				updatedProductValidation[i] = Indicator.None;
				updatedProductDetails[i] = ' ';
			}
		}

		dispatch(
			addBySupcModalSlice.actions.setProductValidation(updatedProductValidation)
		);
		dispatch(
			addBySupcModalSlice.actions.setProductDetails(updatedProductDetails)
		);
		dispatch(evaluateSubmitState());
	}
);

export const evaluateSubmitState = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('addBySupcModal/evaluateSubmitState', async (_, { dispatch, getState }) => {
	const state: IAppState = getState();
	const { productValidation, validationInProgress } = state.addBySupcModal;

	const submitEnabled =
		!validationInProgress && productValidation.indexOf(Indicator.Valid) >= 0;

	dispatch(addBySupcModalSlice.actions.enableSubmit(submitEnabled));
});

export const getProductHeaders = createAsyncThunk<
	IProductHeader[],
	string[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'addBySupcModal/getProductHeader',
	async (productNumbers: string[], { getState }) => {
		const state: IAppState = getState();
		return selectProductHeaders(state, productNumbers);
	}
);

export const setProducts = createAsyncThunk<
	void,
	ISetProductNumbersArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'addBySupcModal/getProductHeader',
	async (args: ISetProductNumbersArgs, { dispatch, getState }) => {
		const state: IAppState = getState();
		const { productNumbers, blueprintId } = args;
		const productValidation: Indicator[] = [];
		const productDetails: string[] = [];
		const needsValidation: string[] = [];

		for (let i = 0; i < productNumbers.length; i++) {
			if (productNumbers[i].trim() === '') {
				productValidation[i] = Indicator.None;
				productDetails[i] = ' ';
			} else if (state.addBySupcModal.productNumbers[i] !== productNumbers[i]) {
				productValidation[i] = Indicator.Loading;
				productDetails[i] = ' ';
				needsValidation.push(productNumbers[i]);
			} else {
				productValidation[i] = state.addBySupcModal.productValidation[i];
				productDetails[i] = state.addBySupcModal.productDetails[i];
			}
		}

		dispatch(addBySupcModalSlice.actions.setProductNumbers(productNumbers));
		dispatch(
			addBySupcModalSlice.actions.setProductValidation(productValidation)
		);
		dispatch(addBySupcModalSlice.actions.setProductDetails(productDetails));

		if (needsValidation.length > 0) {
			const params: FetchProductsByProductNumberParams = {
				productNumbers: needsValidation,
				blueprintId,
			};
			dispatch(validateProductNumbers(params));
		}

		dispatch(evaluateSubmitState());
	}
);

export const addBySupcModalSlice = createSlice({
	name: 'metaProductPage/addBySupcModal',
	initialState,
	reducers: {
		showAddBySupcModal: (state, action) => {
			state.productDetails = initialState.productDetails;
			state.productValidation = initialState.productValidation;
			state.productNumbers = initialState.productNumbers;
			state.submitEnabled = initialState.submitEnabled;

			state.showAddBySupcModal = true;
			state.blueprintId = action.payload.blueprintId;
			state.corpSupplierCode = action.payload.corpSupplierCode;
		},
		hideAddBySupcModal: (state) => {
			state.productDetails = initialState.productDetails;
			state.productValidation = initialState.productValidation;
			state.productNumbers = initialState.productNumbers;
			state.submitEnabled = initialState.submitEnabled;
			state.blueprintId = initialState.blueprintId;
			state.corpSupplierCode = initialState.corpSupplierCode;
			state.validationInProgress = initialState.validationInProgress;
			state.showAddBySupcModal = false;
		},
		enableSubmit: (state, action) => {
			state.submitEnabled = action.payload;
		},
		setProductNumbers: (state, action) => {
			state.productNumbers = action.payload;
		},
		setProductValidation: (state, action) => {
			state.productValidation = action.payload;
		},
		setProductDetails: (state, action) => {
			state.productDetails = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(validateProductNumbers.pending, (state) => {
			state.validationInProgress = true;
		});
		builder.addCase(validateProductNumbers.fulfilled, (state) => {
			state.validationInProgress = false;
		});
		builder.addCase(validateProductNumbers.rejected, (state) => {
			state.validationInProgress = false;
		});
	},
});

const getProductID = (state: IAppState, productNumber: string) =>
	`${state.addBySupcModal.corpSupplierCode}|${productNumber}`;

export const {
	showAddBySupcModal,
	hideAddBySupcModal,
	enableSubmit,
	setProductNumbers,
} = addBySupcModalSlice.actions;
